import {Component, Inject, Input, OnChanges, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  OntologyListData,
  OntologyListEntity,
  RelatableSelectableItem,
  UpdatedFilterTab,
  updatedFilterTabs
} from './FilterBannerModels';
import {NgRedux, select} from '@angular-redux/store';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {DrillDown, FilterCriteria, IAppState, INITIAL_STATE} from '../store/IAppState';
import {
  customGroupsDataChangedTo,
  filterBannerEditChangedTo,
  filterCriteriaChangedTo,
  memberLocationsChangedTo,
  undoDrillDownHistory
} from '../store/actions';
import {OntologyHierarchyService, OntologyHierarchyServiceToken} from '../services/ontology-hierarchy.service';
import {
  setUpBillingAreaList,
  setUpInvoiceList,
  setUpLagPeriodList,
  setUpLocationList,
  setUpOntologyListData,
  setUpPayerHierarchy,
  setUpVisitTypes
} from './FilterCriteriaListSetupHelper';
import {
  CustomGroupsDataAllFilters,
  DateRange,
  DialogData,
  EmDimension,
  InvoiceStatus,
  LagPeriod,
  MemberBillingArea,
  MemberLocation,
  PayerCategoryLevel,
  PayerObject,
  PdfExportData,
  VisitType
} from '../shared/models';
import {
  calculateBreadcrumbTrail,
  calculateSelectedDate,
  deselectAllSelectable,
  filterCriteriaFromSelectedCustomGroup,
  getNewFilterCriteria,
  getPageLabel,
  getSelectedDate,
  isCousin, isPayerLevelMoreGeneral
} from './UpdatedFilterBannerHelpers';
import {PayerCategoryService, PayerCategoryServiceToken} from '../services/payer-category.service';
import {
  DEFAULT_CUSTOM_GROUP_ID,
  DEFAULT_LAG_KEY,
  DEFAULT_MAX_NODE_PATHS,
  DEFAULT_MEMBER_KEY,
  DEFAULT_MEMBER_LOCATION,
  DEFAULT_PAGE_LABEL
} from '../store/DefaultValues';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {UpdatedSaveGroupingComponent} from './updated-save-grouping/updated-save-grouping.component';
import {MemberLocationService, MemberLocationServiceToken} from '../services/member-location.service';
import {FilterBannerTab, PayerType} from '../shared/enums';
import {
  getAppConfigValue,
  getOppositeNodeExpansionState,
  getSelectedDateRange,
  isFeatureEnabled,
  lagPeriods,
  nodeExpansionIsActive,
  Page,
  shallowCopyOf
} from '../shared/helpers';
import {MemberBillingAreaService, MemberBillingAreaServiceToken} from '../services/member-billing-area.service';
import {SearchProviderComponent} from './search-provider/search-provider.component';
import {SearchLocationComponent} from './search-location/search-location.component';
import {SelectPayerComponent} from './select-payer/select-payer.component';
import {SearchBillingAreasComponent} from './search-billing-areas/search-billing-areas.component';
import {SearchLagKeyComponent} from './search-lag-key/search-lag-key.component';
import {InvoiceComponent} from './invoice/invoice.component';
import {VisitTypeComponent} from './visit-type/visit-type.component';
import {ProviderLimitExceededComponent} from './provider-limit-exceeded/provider-limit-exceeded.component';
import {FeatureToggleEntries} from '../shared/feature-toggle-settings-enum';
import {SaveApplyDialogComponent} from '../shared/components/save-apply-dialog/save-apply-dialog.component';
import {DrillDownService} from '../services/drilldown.service';
import {areFiltersEqual, determineAllowedDataset} from '../shared/compare-helpers';
import {take} from 'rxjs/operators';
import * as _ from 'lodash';
import {AppConfigEntries} from '../shared/app-config-settings-enum';
import {ExportPdfDialogComponent} from '../export-pdf-dialog/export-pdf-dialog.component';
import {invoiceStatusTypes, visitTypes} from '../shared/constants';
import {MultiSearchLocationComponent} from './multi-search-location/multi-search-location.component';
import {CustomGroupsService, CustomGroupsServiceToken} from '../filter-banner/services/custom-groups-service';
import {getSelectedNodeCountsFromNodePath} from '../shared/ontology-helpers';
import {ChangeableCustomGroup, createNewCustomGroupFromFilter, GroupDialogEvent} from './manage-groups-helper';
import {AcknowledgeDialogComponent} from '../shared/components/acknowledge-dialog/acknowledge-dialog.component';

@Component({
  selector: 'app-updated-filter-banner',
  templateUrl: './updated-filter-banner.component.html',
  styleUrls: ['./updated-filter-banner.component.scss']
})
export class UpdatedFilterBannerComponent implements OnInit, OnChanges, OnDestroy {

  @Input() page: Page;
  @Input() shouldPayersBeGranular = false;
  @Input() payerPermitted = true;
  @Input() billingsPermitted = true;
  @Input() lagPermitted = true;
  @Input() invoicePermitted = true;
  @Input() visitTypePermitted = true;
  @select(['filters'])
  private readonly filters$: Observable<FilterCriteria>;
  @select(['display', 'filterBannerEdit'])
  private readonly filterBannerEdit$: Observable<boolean>;
  @select(['data', 'customGroupsData'])
  private readonly customGroupsData$: Observable<CustomGroupsDataAllFilters[]>;
  @select(['display', 'drillDownHistory'])
  private readonly drillDownHistory$: Observable<DrillDown[]>;

  editMode = false;
  pageLabel = DEFAULT_PAGE_LABEL;
  private originalFilters: FilterCriteria | undefined = undefined;
  readonly updatedFilterTabs = updatedFilterTabs;
  updatedBannerTabs = this.updatedFilterTabs.filter(tb => tb.onMainBanner);
  FilterBannerTab = FilterBannerTab;
  selectedTab: FilterBannerTab = FilterBannerTab.PROVIDER;
  unsavedData: boolean;
  maxNodePaths = DEFAULT_MAX_NODE_PATHS;
  private canShowMultiLocations = false;
  isDrillDown: boolean;
  private originalMemberKey: number;
  private originalNodePath: string;
  private subscription: Subscription;
  dialogActionListener: BehaviorSubject<GroupDialogEvent>;
  private dialogActionSubscription: Subscription;

  readonly maxCharsForNodePath = 60;

  @ViewChild('pdfContent', {static: true}) pdfContent: ExportPdfDialogComponent;
  // For custom groups
  selectedGroup: CustomGroupsDataAllFilters | undefined = undefined;
  newlyProposedCustomGroup: CustomGroupsDataAllFilters | undefined = undefined;
  newlyProposedFilterCriteria: FilterCriteria | undefined = undefined;
  showGroupings = false;
  customGroups: CustomGroupsDataAllFilters[] | undefined = undefined;
  theDefaultGroup: CustomGroupsDataAllFilters | undefined = undefined;
  // For the ontology tab
  @ViewChild(SearchProviderComponent) providerComponent: SearchProviderComponent;
  readonly providerTab = this.updatedFilterTabs.find(tb => tb.tab === FilterBannerTab.PROVIDER);
  ontologyListData: OntologyListData | undefined = undefined;
  breadcrumb = '';
  // For the date range tab
  selectedDateForRangeCalendar: any;
  selectedDateRange: DateRange;
  // For the location tab
  @ViewChild(SearchLocationComponent) locationComponent: SearchLocationComponent;
  readonly locationTab = this.updatedFilterTabs.find(tb => tb.tab === FilterBannerTab.LOCATION);
  locations: RelatableSelectableItem<MemberLocation>[] = [];
  locationLoadingIndicator = true;
  // For the payer tab
  @ViewChild(SelectPayerComponent) payerComponent: SelectPayerComponent;
  readonly payerTab = this.updatedFilterTabs.find(tb => tb.tab === FilterBannerTab.PAYER);
  payers: RelatableSelectableItem<PayerObject>[] = [];
  payerLoadingIndicator = true;
  // For the billing tab
  @ViewChild(SearchBillingAreasComponent) billingComponent: SearchBillingAreasComponent;
  readonly billingTab = this.updatedFilterTabs.find(tb => tb.tab === FilterBannerTab.BILLING);
  billingAreas: RelatableSelectableItem<MemberBillingArea>[] = [];
  billingLoadingIndicator = true;
  // For the lag tab
  @ViewChild(SearchLagKeyComponent) lagComponent: SearchLagKeyComponent;
  readonly lagTab = this.updatedFilterTabs.find(tb => tb.tab === FilterBannerTab.LAG);
  lagPeriods: RelatableSelectableItem<LagPeriod>[] = [];
  // For the invoice tab
  @ViewChild(InvoiceComponent) invoiceComponent: InvoiceComponent;
  readonly invoiceTab = this.updatedFilterTabs.find(tb => tb.tab === FilterBannerTab.INVOICE_STATUS);
  invoiceStatusTypes: RelatableSelectableItem<InvoiceStatus>[] = [];
  // For the visit type tab
  @ViewChild(VisitTypeComponent) visitComponent: VisitTypeComponent;
  readonly visitTab = this.updatedFilterTabs.find(tb => tb.tab === FilterBannerTab.VISIT_TYPE);
  visitTypes: RelatableSelectableItem<VisitType>[] = [];
  // For the multi location tab
  @ViewChild(MultiSearchLocationComponent) multiLocationComponent: MultiSearchLocationComponent;
  readonly multiLocationTab = this.updatedFilterTabs.find(tb => tb.tab === FilterBannerTab.MULTI_LOCATION);
  private numberOfMultipleLocationsSelected = 0;

  private static failedApply(component: UpdatedFilterBannerComponent): void {
    component.dialog.open(ProviderLimitExceededComponent, {
      data: {
        maxNodePaths: component.maxNodePaths
      }
    });
  }

  constructor(private ngRedux: NgRedux<IAppState>,
              private dialog: MatDialog,
              private drillDownService: DrillDownService,
              @Inject(OntologyHierarchyServiceToken) private ontologyService: OntologyHierarchyService,
              @Inject(CustomGroupsServiceToken) private customGroupService: CustomGroupsService,
              @Inject(MemberLocationServiceToken) private locationService: MemberLocationService,
              @Inject(PayerCategoryServiceToken) private payerService: PayerCategoryService,
              @Inject(MemberBillingAreaServiceToken) private billingService: MemberBillingAreaService) { }

  ngOnInit(): void {
    this.dialogActionListener = new BehaviorSubject<GroupDialogEvent>(GroupDialogEvent.INITIAL);
    this.listenForDialogActions();
    this.subscription = combineLatest([this.filters$, this.customGroupsData$, this.filterBannerEdit$,
      this.drillDownHistory$]).subscribe(
      ([filters, customGroups, editMode, drillDownHistory]: [FilterCriteria, CustomGroupsDataAllFilters[], boolean, DrillDown[]]) => {
        this.isDrillDown = !!drillDownHistory.length;
        const { featureToggleSettings, userProfile } = this.ngRedux.getState().data;
        this.canShowMultiLocations = isFeatureEnabled(FeatureToggleEntries.LOCATION_MULTI_SELECT, featureToggleSettings, userProfile);
        this.showOrHideTabsBasedOnFeatureToggle();
        this.maxNodePaths = +getAppConfigValue(AppConfigEntries.MAX_PROVIDERS,
          this.ngRedux.getState().data.applicationConfigurationSettings);
        const newMemberKey = this.originalMemberKey !== filters.memberKey;
        const newNodePath = this.originalNodePath !== filters.nodePath;
        if (newMemberKey || newNodePath) {
          this.createData(filters, {
            nodePath: newNodePath,
            memberKey: newMemberKey
          });
        }
        this.originalMemberKey = filters.memberKey;
        this.originalNodePath = filters.nodePath;
        if (editMode && !this.editMode) {
          this.unsavedData = false;
          this.originalFilters = shallowCopyOf<FilterCriteria>(filters);
          this.originalFilters.dateRange.fiscalYear = filters.dateRange.fiscalYear;
          this.recordOriginalSelections();
        } else if (!editMode) {
          this.selectedTab = -1;
          this.customGroups = customGroups;
          const deletionDetected = this.selectedGroup && !this.customGroups.find(group => group.id ===
            this.selectedGroup?.id);
          this.selectedGroup = customGroups?.find(group => areFiltersEqual(filters,
            filterCriteriaFromSelectedCustomGroup(group, this.ngRedux.getState()), false, true));
          this.selectedDateForRangeCalendar = calculateSelectedDate(filters.dateRange);
          this.theDefaultGroup = customGroups.find(g => g.isDefault);
          this.selectedGroup = this.selectedGroup ? this.selectedGroup : deletionDetected ? this.theDefaultGroup : undefined;
          this.initializeSelections(this.selectedGroup ? filterCriteriaFromSelectedCustomGroup(this.selectedGroup,
            this.ngRedux.getState()) : filters);
          this.breadcrumb = calculateBreadcrumbTrail(this.ontologyListData);
          if (!this.originalFilters) {
            this.selectedDateRange = filters.dateRange;
            this.setDateTab();
          }
        }
        this.editMode = editMode;
      });
  }

  ngOnChanges(): void {
    this.pageLabel = getPageLabel(this.page);
    if (this.payerTab) {
      this.payerTab.available = this.payerPermitted;
      this.showPayerLabelOfMostGranular();
    }
    if (this.billingTab) {
      this.billingTab.available = this.billingsPermitted;
    }
    if (this.lagTab) {
      this.lagTab.available = this.lagPermitted;
    }
    if (this.invoiceTab) {
      this.invoiceTab.available = this.invoicePermitted;
    }
    if (this.visitTab) {
      this.visitTab.available = this.visitTypePermitted;
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.dialogActionSubscription?.unsubscribe();
  }

  private getDialogActionListener(): Observable<GroupDialogEvent> {
    return this.dialogActionListener.asObservable();
  }

  private listenForDialogActions(): void {
    this.dialogActionSubscription = this.getDialogActionListener().subscribe((value: GroupDialogEvent) => {
      switch (value) {
        case GroupDialogEvent.APPLY:
          this.applyWithoutSavingGroup();
          break;
        case GroupDialogEvent.TENTATIVE_SAVE:
          this.openSaveDialog();
          break;
        case GroupDialogEvent.DEFINITE_SAVE:
          this.onGroupConfirmSave(false);
          break;
        case GroupDialogEvent.UPDATE:
          this.onGroupConfirmSave(true);
      }
    });
  }

  exportToPdf(event: [PdfExportData, EmDimension[], boolean]): void {
    if (!event[1].length) {
      this.pdfContent.export(event[0], event[2]);
    } else {
      this.pdfContent.exportWithPageBreaks(...event);
    }
  }

  private showOrHideTabsBasedOnFeatureToggle(): void {
    this.updatedBannerTabs = this.updatedFilterTabs.filter(tb => tb.onMainBanner);
    if (this.canShowMultiLocations) {
      this.updatedBannerTabs = this.updatedBannerTabs.filter(tb => tb.tab !== FilterBannerTab.LOCATION);
    } else {
      this.updatedBannerTabs = this.updatedBannerTabs.filter(tb => tb.tab !== FilterBannerTab.MULTI_LOCATION);
    }
  }

  private initializeSelections(filters: FilterCriteria): void {
    this.initializeProviderSelections(filters);
    if (!this.canShowMultiLocations) {
      this.initializeLocationSelections(filters);
    }
    this.initializePayerSelections(filters);
    this.initializeBillingSelections(filters);
    this.initializeLagSelections(filters);
    this.initializeInvoiceSelections(filters);
    this.initializeVisitSelections(filters);
    if (this.canShowMultiLocations) {
      this.initializeMultiLocationSelections(filters);
    }
  }

  private initializeProviderSelections(filters: FilterCriteria): void {
    (this.ontologyListData?.providers || []).forEach(provider => {
      provider.item.selected = false;
      filters.nodePath.split('|').forEach(subPath => {
        if (provider.item.item.nodePath.includes(subPath)) {
          provider.item.selected = true;
        }
      });
    });
    (this.ontologyListData?.specialties || []).concat(this.ontologyListData?.departments || []).forEach(node => {
      node.item.selected = !!node.relatives.find(r => r.item.providerNodeName && r.selected);
    });
  }

  private initializeLocationSelections(filters: FilterCriteria): void {
    let oneSelected = false;
    for (let i = 0; i < this.locations.length; i++) {
      const location = this.locations[i];
      location.item.selected = filters.memberLocation.memberLocationKey === location.item.key;
      if (location.item.selected) {
        oneSelected = true;
        this.onFiltersChanged(this.locationTab, location);
      }
      if (oneSelected) {
        // TODO: If we have location multiselect in the future, there will be no need to break this loop.
        // TODO: What do you know? That day has come
        break;
      }
    }
    if (!oneSelected) {
      this.onFiltersChanged(this.locationTab);
    }
  }

  private initializeMultiLocationSelections(filters: FilterCriteria): void {
    this.numberOfMultipleLocationsSelected = 0;
    for (let i = 0; i < this.locations.length; i++) {
      const location = this.locations[i];
      location.item.selected = !!filters.memberLocationKeys.split('|').map(x => +x).find(key => key === location.item.key);
      if (location.item.selected) {
        ++this.numberOfMultipleLocationsSelected;
        this.onFiltersChanged(this.multiLocationTab, location);
      }
    }
    if (this.numberOfMultipleLocationsSelected > 0 && this.numberOfMultipleLocationsSelected < this.locations.length) {
      this.onFiltersChanged(this.multiLocationTab);
    }
  }

  private initializePayerSelections(filters: FilterCriteria): void {
    let oneSelected = false;
    for (let i = 0; i < this.payers.length; i++) {
      const payer = this.payers[i];
      switch (payer.item.item.level) {
        case PayerType.CATEGORY:
          payer.item.selected = filters.payerType === PayerType.CATEGORY &&
            filters.payerCategory.payerCategoryKey === payer.item.key;
          if (payer.item.selected) {
            oneSelected = true;
          }
          break;
        case PayerType.NATIONAL:
          payer.item.selected = filters.payerType === PayerType.NATIONAL &&
            filters.payerKey === payer.item.key;
          if (payer.item.selected) {
            oneSelected = true;
            payer.relatives.filter(r => r.item.level === PayerType.CATEGORY).forEach(rel => {
              if (rel.expansion && !nodeExpansionIsActive(rel.expansion.state)) {
                rel.expansion.state = getOppositeNodeExpansionState(rel.expansion.state);
                this.payers.filter(x => isCousin(x, rel) && x.item.expansion &&
                  nodeExpansionIsActive(x.item.expansion.state)).forEach(p => {
                    if (p.item.expansion) {
                      p.item.expansion.state = getOppositeNodeExpansionState(p.item.expansion.state);
                    }
                });
              }
            });
          }
          break;
        case PayerType.MEMBER:
          payer.item.selected = filters.payerType === PayerType.MEMBER &&
            filters.payerKey === payer.item.key;
          if (payer.item.selected) {
            oneSelected = true;
            payer.relatives.filter(r => r.item.level === PayerType.CATEGORY).forEach(rel => {
              if (rel.expansion && !nodeExpansionIsActive(rel.expansion.state)) {
                rel.expansion.state = getOppositeNodeExpansionState(rel.expansion.state);
                this.payers.filter(x => isCousin(x, rel) && x.item.expansion &&
                  nodeExpansionIsActive(x.item.expansion.state)).forEach(p => {
                  if (p.item.expansion) {
                    p.item.expansion.state = getOppositeNodeExpansionState(p.item.expansion.state);
                  }
                });
              }
            });
            payer.relatives.filter(r => r.item.level === PayerType.NATIONAL).forEach(rel => {
              if (rel.expansion && !nodeExpansionIsActive(rel.expansion.state)) {
                rel.expansion.state = getOppositeNodeExpansionState(rel.expansion.state);
                this.payers.filter(x => isCousin(x, rel) && x.item.expansion &&
                  nodeExpansionIsActive(x.item.expansion.state)).forEach(p => {
                  if (p.item.expansion) {
                    p.item.expansion.state = getOppositeNodeExpansionState(p.item.expansion.state);
                  }
                });
              }
            });
          }
      }
      if (oneSelected) {
        // TODO: If we have payer multiselect in the future, there will be no need to break this loop.
        this.showPayerLabelOfMostGranular();
        break;
      }
    }
    if (!oneSelected) {
      this.onFiltersChanged(this.payerTab);
    }
  }

  private showPayerLabelOfMostGranular(): void {
    let selectedPayer: RelatableSelectableItem<PayerObject> | undefined;
    let changed: boolean;
    do {
      changed = false;
      this.payers.filter(p => p.item.selected).forEach(x => {
        selectedPayer = !selectedPayer || (isPayerLevelMoreGeneral(selectedPayer.item.item.level, x.item.item.level)
          && SelectPayerComponent.payerGranularityAllowed(this.shouldPayersBeGranular, x.item.item)) ? x : selectedPayer;
        x.relatives.filter(r => isPayerLevelMoreGeneral(r.item.level, x.item.item.level)).forEach(rel => {
          if (!rel.selected) {
            changed = true;
            selectedPayer = undefined;
          }
          rel.selected = true;
        });
      });
    } while (changed);
    this.onFiltersChanged(this.payerTab, selectedPayer);
  }

  private initializeBillingSelections(filters: FilterCriteria): void {
    let oneSelected = false;
    for (let i = 0; i < this.billingAreas.length; i++) {
      const bill = this.billingAreas[i];
      bill.item.selected = filters.memberBillingArea.memberBillingAreaKey === bill.item.key;
      if (bill.item.selected) {
        oneSelected = true;
        this.onFiltersChanged(this.billingTab, bill);
      }
      if (oneSelected) {
        // TODO: If we have billing area multiselect in the future, there will be no need to break this loop.
        break;
      }
    }
    if (!oneSelected) {
      this.onFiltersChanged(this.billingTab);
    }
  }

  private initializeLagSelections(filters: FilterCriteria): void {
    let oneSelected = false;
    for (let i = 0; i < this.lagPeriods.length; i++) {
      const lag = this.lagPeriods[i];
      lag.item.selected = filters.lagKey === lag.item.key;
      if (lag.item.selected) {
        oneSelected = true;
        this.onFiltersChanged(this.lagTab, lag);
      }
      if (oneSelected) {
        // TODO: If we have lag multiselect in the future, there will be no need to break this loop.
        break;
      }
    }
    if (!oneSelected) {
      this.onFiltersChanged(this.lagTab);
    }
  }

  private initializeInvoiceSelections(filters: FilterCriteria): void {
    let oneSelected = false;
    this.invoiceStatusTypes.forEach(invoice => {
      invoice.item.selected = filters.invoiceStatus === invoice.item.key;
      if (invoice.item.selected) {
        oneSelected = true;
        this.onFiltersChanged(this.invoiceTab, invoice);
      }
    });
    if (!oneSelected) {
      this.onFiltersChanged(this.invoiceTab);
    }
  }

  private initializeVisitSelections(filters: FilterCriteria): void {
    let oneSelected = false;
    this.visitTypes.forEach(visit => {
      visit.item.selected = filters.telehealthFlag === visit.item.key;
      if (visit.item.selected) {
        oneSelected = true;
        this.onFiltersChanged(this.visitTab, visit);
      }
    });
    if (!oneSelected) {
      this.onFiltersChanged(this.visitTab);
    }
  }

  private createData(filters: FilterCriteria, filtersChanged: FilterChanges): void {
    if (filters.memberKey < 0) {
      return;
    }
    if (filtersChanged.memberKey) {
      this.locationLoadingIndicator = true;
      this.locationService.getMemberLocations(getNewFilterCriteria(filters, this.ontologyListData?.allDepartmentsNodePath || '',
          this.selectedDateRange, this.ontologyListData?.providers || [], this.locations,
          this.payers, this.billingAreas, this.lagPeriods, this.invoiceStatusTypes, this.visitTypes)
        || filters).pipe(take(1)).subscribe((locations: MemberLocation[]) => {
        this.ngRedux.dispatch(memberLocationsChangedTo(locations.map(x => {
          return {...x, currentlySelected: false};
        })));
        this.locations = setUpLocationList(locations);
        this.locationLoadingIndicator = false;
        this.initializeLocationSelections(filters);
      });
      this.ontologyService.getOntologyList(filters).pipe(take(1)).subscribe((ontologyList: OntologyListEntity[]) => {
        this.ontologyListData = setUpOntologyListData(ontologyList);
        this.initializeProviderSelections(filters);
        this.breadcrumb = calculateBreadcrumbTrail(this.ontologyListData);
      });
      this.payerLoadingIndicator = true;
      this.payerService.getPayerHierarchy(filters.memberKey).pipe(take(1)).subscribe((payerHierarchy: PayerCategoryLevel[]) => {
        this.payers = setUpPayerHierarchy(payerHierarchy);
        this.payerLoadingIndicator = false;
        this.initializePayerSelections(filters);
      });
      this.billingLoadingIndicator = true;
      this.billingService.getMemberBillingArea(filters.memberKey).pipe(take(1)).subscribe((billingAreas: MemberBillingArea[]) => {
        this.billingAreas = setUpBillingAreaList(billingAreas);
        this.billingLoadingIndicator = false;
        this.initializeBillingSelections(filters);
      });
    } else if (filtersChanged.nodePath) {
      this.locationLoadingIndicator = true;
      this.locationService.getMemberLocations(getNewFilterCriteria(filters, this.ontologyListData?.allDepartmentsNodePath || '',
          this.selectedDateRange, this.ontologyListData?.providers || [], this.locations, this.payers,
          this.billingAreas, this.lagPeriods, this.invoiceStatusTypes, this.visitTypes)
        || filters).pipe(take(1)).subscribe((locations: MemberLocation[]) => {
        this.locations = setUpLocationList(locations);
        this.locationLoadingIndicator = false;
        this.initializeLocationSelections(filters);
      });
    }
    this.lagPeriods = this.lagPeriods.length ? this.lagPeriods : setUpLagPeriodList(lagPeriods.filter(lag =>
      lag.key !== DEFAULT_LAG_KEY));
    this.initializeLagSelections(filters);
    this.visitTypes = this.visitTypes.length ? this.visitTypes : setUpVisitTypes(visitTypes.filter(vis => !vis.all));
    this.initializeVisitSelections(filters);
    this.invoiceStatusTypes = this.invoiceStatusTypes.length ? this.invoiceStatusTypes :
      setUpInvoiceList(invoiceStatusTypes.filter(status => !status.all));
    this.initializeInvoiceSelections(filters);
  }

  private recordOriginalSelections(): void {
    this.setDateTab();
    if (this.providerTab) {
      this.providerTab.newSelections = { selections: 0, deselections: 0 };
    }
    this.ontologyListData?.providers.concat(this.ontologyListData?.specialties).concat(this.ontologyListData?.departments)
      .forEach(node => {
      node.item.originallySelected = node.item.selected;
    });
    if (this.locationTab) {
      this.locationTab.originalLabel = this.locationTab.defaultLabel;
      this.locationTab.newSelections = { selections: 0, deselections: 0 };
      this.locations.forEach(location => {
        location.item.originallySelected = location.item.selected;
        if (location.item.selected) {
          // @ts-ignore
          this.locationTab.originalLabel = location.item.displayText;
        }
      });
    }
    if (this.payerTab) {
      this.payerTab.originalLabel = this.payerTab.defaultLabel;
      this.payerTab.newSelections = { selections: 0, deselections: 0 };
      this.payers.forEach(payer => {
        payer.item.originallySelected = payer.item.selected;
        if (payer.item.selected) {
          // @ts-ignore
          this.payerTab.originalLabel = payer.item.displayText;
        }
      });
    }
    if (this.billingTab) {
      this.billingTab.originalLabel = this.billingTab.defaultLabel;
      this.billingTab.newSelections = { selections: 0, deselections: 0 };
      this.billingAreas.forEach(billing => {
        billing.item.originallySelected = billing.item.selected;
        if (billing.item.selected) {
          // @ts-ignore
          this.billingTab.originalLabel = billing.item.displayText;
        }
      });
    }
    if (this.lagTab) {
      this.lagTab.originalLabel = this.lagTab.defaultLabel;
      this.lagTab.newSelections = { selections: 0, deselections: 0 };
      this.lagPeriods.forEach(lag => {
        lag.item.originallySelected = lag.item.selected;
        if (lag.item.selected) {
          // @ts-ignore
          this.lagTab.originalLabel = lag.item.displayText;
        }
      });
    }
    if (this.invoiceTab) {
      this.invoiceTab.originalLabel = this.invoiceTab.defaultLabel;
      this.invoiceTab.newSelections = { selections: 0, deselections: 0 };
      this.invoiceStatusTypes.forEach(iv => {
        iv.item.originallySelected = iv.item.selected;
        if (iv.item.selected) {
          // @ts-ignore
          this.invoiceTab.originalLabel = iv.item.displayText;
        }
      });
    }
    if (this.visitTab) {
      this.visitTab.originalLabel = this.visitTab.defaultLabel;
      this.visitTab.newSelections = { selections: 0, deselections: 0 };
      this.visitTypes.forEach(vt => {
        vt.item.originallySelected = vt.item.selected;
        if (vt.item.selected) {
          // @ts-ignore
          this.visitTab.originalLabel = vt.item.displayText;
        }
      });
    }
    if (this.multiLocationTab) {
      this.multiLocationTab.originalLabel = this.multiLocationTab.defaultLabel;
      this.multiLocationTab.newSelections = { selections: 0, deselections: 0 };
      this.locations.forEach(location => {
        location.item.originallySelected = location.item.selected;
      });
    }
  }

  private createNewFilterCriteria(): FilterCriteria {
    if (!this.newlyProposedFilterCriteria) {
      this.newlyProposedFilterCriteria = getNewFilterCriteria(this.originalFilters || INITIAL_STATE.filters,
        this.ontologyListData?.allDepartmentsNodePath || '',
        this.selectedDateRange,
        this.ontologyListData?.providers || [],
        this.locations || [],
        this.payers || [],
        this.billingAreas || [],
        this.lagPeriods || [],
        this.invoiceStatusTypes || [],
        this.visitTypes || []);
    }
    return this.newlyProposedFilterCriteria || INITIAL_STATE.filters;
  }

  private createNewCustomGroup(): CustomGroupsDataAllFilters {
    this.newlyProposedFilterCriteria = this.createNewFilterCriteria();
    if (!this.newlyProposedCustomGroup) {
      const state = this.ngRedux.getState();
      const selectionCounts = getSelectedNodeCountsFromNodePath(this.newlyProposedFilterCriteria.nodePath);
      const dataSet = determineAllowedDataset({
        ...state, display: {
          ...state.display, selectedProviders:
          selectionCounts.selectedProviders, selectedSpecialties: selectionCounts.selectedSpecialties
        }
      });
      this.newlyProposedCustomGroup = createNewCustomGroupFromFilter(this.newlyProposedFilterCriteria, false,
        false, this.selectedGroup?.name || '', dataSet);
    }
    return this.newlyProposedCustomGroup;
  }

  private setDateTab(): UpdatedFilterTab | undefined {
    const dateTab = this.updatedFilterTabs.find(tab => tab.tab === FilterBannerTab.DATE);
    if (dateTab) {
      dateTab.label = getSelectedDateRange(this.selectedDateRange, true);
      if (!_.isEqual(this.originalFilters?.dateRange, this.selectedDateRange)) {
        dateTab.newSelections = { selections: 1, deselections: 1 };
      } else {
        dateTab.newSelections = { selections: 0, deselections: 0 };
      }
    }
    return dateTab;
  }

  setTab(tab?: UpdatedFilterTab): void {
    if (tab && !tab.available && !this.editMode) {
      return;
    }
    this.ngRedux.dispatch(filterBannerEditChangedTo(true));
    this.selectedTab = tab ? tab.tab : FilterBannerTab.PROVIDER;
  }

  onDateChange(inputDateRange: any): void {
    if (inputDateRange) {
      this.selectedDateRange = {
        startMonth: inputDateRange.value.begin.getMonth() + 1,
        startYear: inputDateRange.value.begin.getFullYear(),
        endMonth: inputDateRange.value.end.getMonth() + 1,
        endYear: inputDateRange.value.end.getFullYear(),
        selectedDateRangeOption: inputDateRange.value.selectedDateRangeOption,
        fiscalYear: inputDateRange.value.fiscalYear
      };
      this.selectedDateForRangeCalendar = getSelectedDate(this.selectedDateRange);
      const dateTab = this.setDateTab();
      this.onFiltersChanged(dateTab);
    }
  }

  onFiltersChanged = (tab?: UpdatedFilterTab, item?: RelatableSelectableItem<any>) => {
    this.newlyProposedCustomGroup = undefined;
    this.newlyProposedFilterCriteria = undefined;
    if (tab) {
      if (tab.onMainBanner && tab.tab !== FilterBannerTab.DATE && tab.tab !== FilterBannerTab.MULTI_LOCATION) {
        tab.label = (item?.item.displayText || tab.defaultLabel) + '';
      } else if (tab.tab === FilterBannerTab.MULTI_LOCATION) {
        tab.label = this.numberOfMultipleLocationsSelected === 0 || this.numberOfMultipleLocationsSelected ===
          this.locations.length ? DEFAULT_MEMBER_LOCATION.memberLocationName : `${this.numberOfMultipleLocationsSelected} locations`;
      }
      this.unsavedData = !!(this.updatedFilterTabs).find(tb => {
        const tabSelections = tb.newSelections;
        return tabSelections && (tabSelections.selections || tabSelections.deselections);
      });
    }
  };

  selectGroup(group: CustomGroupsDataAllFilters): void {
    this.selectedGroup = group;
    const filterCriteria = filterCriteriaFromSelectedCustomGroup(<CustomGroupsDataAllFilters>group, this.ngRedux.getState());
    this.showGroupings = false;
    this.unsavedData = false;
    this.ngRedux.dispatch(filterCriteriaChangedTo(filterCriteria));
    this.ngRedux.dispatch(undoDrillDownHistory(true));
    this.ngRedux.dispatch(filterBannerEditChangedTo(false));
  }

  openSaveDialog(): void {
    this.newlyProposedCustomGroup = this.createNewCustomGroup();
    this.dialog.open(UpdatedSaveGroupingComponent, {
      data: {
        group: this.newlyProposedCustomGroup,
        existingGroups: this.customGroups,
        dialogActionListener: this.dialogActionListener
      }
    });
  }

  onGroupConfirmSave(groupIsEdited: boolean): void {
    this.newlyProposedCustomGroup = this.createNewCustomGroup();
    if (groupIsEdited) {
      const editedGroup = this.customGroups?.find(cg => cg.name.toLowerCase().localeCompare(
        this.newlyProposedCustomGroup?.name || '') === 0);
      this.customGroupService.updateCustomGroup({
        ...this.newlyProposedCustomGroup, id: editedGroup?.id || 0
      }, this.newlyProposedCustomGroup.memberKey, false).subscribe(() => {
        this.afterSave(this.newlyProposedCustomGroup?.memberKey || DEFAULT_MEMBER_KEY);
      });
    } else {
      this.customGroupService.saveCustomGroup(this.newlyProposedCustomGroup, this.newlyProposedCustomGroup.memberKey)
        .subscribe((savedGroup: CustomGroupsDataAllFilters) => {
          this.afterSave(savedGroup.memberKey);
        });
    }
  }

  private afterSave(memberKey: number): void {
    this.customGroupService.getCustomGroups(memberKey)
      .subscribe((groups: CustomGroupsDataAllFilters[]) => {
        const futureListOfChangeableGroups: ChangeableCustomGroup[] = groups.map(x => {
          return {
            original: x,
            newName: x.name,
            newDefault: x.isDefault,
            newPreload: x.preload,
            duplicateAttempt: false
          };
        });
        futureListOfChangeableGroups.forEach(x => {
          (x.original as CustomGroupsDataAllFilters).preload = x.newPreload;
          x.original.isDefault = x.newDefault;
          this.customGroupService.updateCustomGroup(x.original as CustomGroupsDataAllFilters, memberKey,
            false).subscribe();
        });
        this.ngRedux.dispatch(customGroupsDataChangedTo(groups));
      });
    this.provideFeedbackToUser();
    this.ngRedux.dispatch(undoDrillDownHistory(true));
  }

  private provideFeedbackToUser() {
    let dialogRef: MatDialogRef<AcknowledgeDialogComponent, DialogData>;
    const dialogData: DialogData = {
      title: 'Saved!',
      question: 'Your updates have been saved.',
      confirmCallback() {
        dialogRef.close();
      }
    };
    dialogRef = this.dialog.open(AcknowledgeDialogComponent, {
      data: dialogData
    });
  }

  restoreDefault(): void {
    if (!this.theDefaultGroup) {
      return;
    }
    const filterCriteria = filterCriteriaFromSelectedCustomGroup(<CustomGroupsDataAllFilters>this.theDefaultGroup,
    this.ngRedux.getState());
    this.ngRedux.dispatch(filterCriteriaChangedTo(filterCriteria));
    this.ngRedux.dispatch(filterBannerEditChangedTo(false));
  }

  clearSelections(): void {
    this.selectedGroup = undefined;
    deselectAllSelectable(this.ontologyListData?.departments.concat(this.ontologyListData.specialties) || []);
    deselectAllSelectable(this.ontologyListData?.providers || [], this.providerTab);
    deselectAllSelectable(this.locations, this.locationTab);
    deselectAllSelectable(this.payers, this.payerTab);
    deselectAllSelectable(this.lagPeriods, this.lagTab);
    deselectAllSelectable(this.invoiceStatusTypes, this.invoiceTab);
    deselectAllSelectable(this.visitTypes, this.visitTab);
    deselectAllSelectable(this.billingAreas, this.billingTab);
    this.providerComponent?.ngOnInit();
    this.locationComponent?.ngOnInit();
    this.payerComponent?.ngOnInit();
    this.billingComponent?.ngOnInit();
    this.lagComponent?.ngOnInit();
    this.invoiceComponent?.ngOnInit();
    this.visitComponent?.ngOnInit();
    this.updatedFilterTabs.forEach(tab => {
      this.onFiltersChanged(tab);
    });
    this.setDateTab();
  }

  cancel(): void {
    this.selectedDateRange = this.originalFilters?.dateRange || this.selectedDateRange;
    this.setDateTab();
    this.ontologyListData?.providers.concat(this.ontologyListData?.specialties).concat(this.ontologyListData?.departments)
      .forEach(node => {
        node.item.selected = !!node.item.originallySelected;
      });
    if (this.locationTab) {
      this.locationTab.label = this.locationTab.originalLabel + '';
    }
    this.locations.forEach(loc => {
      loc.item.selected = !!loc.item.originallySelected;
    });
    if (this.payerTab) {
      this.payerTab.label = this.payerTab.originalLabel + '';
    }
    this.payers.forEach(pay => {
      pay.item.selected = !!pay.item.originallySelected;
    });
    if (this.billingTab) {
      this.billingTab.label = this.billingTab.originalLabel + '';
    }
    this.billingAreas.forEach(bil => {
      bil.item.selected = !!bil.item.originallySelected;
    });
    this.ngRedux.dispatch(filterBannerEditChangedTo(false));
  }

  apply(): void {
    if (!this.unsavedData) {
      return;
    }
    this.newlyProposedCustomGroup = this.createNewCustomGroup();
    this.dialog.open(SaveApplyDialogComponent, {
      data: {
        title: 'Save changes?',
        question: 'Would you like to save the selected providers and filters for faster loading?',
        confirmButtonAltText: 'Save',
        dialogActionListener: this.dialogActionListener
      }
    });
  }

  applyWithoutSavingGroup(): void {
    if ((this.originalFilters || INITIAL_STATE.filters).nodePath.split('|').length <= this.maxNodePaths) {
      this.successfulApply();
    } else {
      UpdatedFilterBannerComponent.failedApply(this);
    }
  }

  successfulApply(): void {
    this.ngRedux.dispatch(filterCriteriaChangedTo({
      ...this.newlyProposedFilterCriteria || this.originalFilters || INITIAL_STATE.filters,
      customGroupId: DEFAULT_CUSTOM_GROUP_ID
    }));
    const currentLocations = this.ngRedux.getState().data.memberLocations;
    currentLocations.forEach(loc => {
      loc.currentlySelected = this.newlyProposedFilterCriteria ? !!this.newlyProposedFilterCriteria.memberLocationKeys
          .split('|').find(key => +key === loc.memberLocationKey) : false;
    });
    this.ngRedux.dispatch(memberLocationsChangedTo(currentLocations));
    this.ngRedux.dispatch(filterBannerEditChangedTo(false));
    this.ngRedux.dispatch(undoDrillDownHistory(true));
  }

  navigateBack() {
    if (this.editMode) {
      return;
    }
    this.drillDownService.drillUp();
  }
}

interface FilterChanges {
  nodePath: boolean;
  memberKey: boolean;
}

export class InputDateRange {
  private readonly month: number;
  private readonly fullYear: number;

  constructor(month: number, fullYear: number) {
    this.month = month - 1;
    this.fullYear = fullYear;
  }

  getMonth(): number {
    return this.month;
  }

  getFullYear(): number {
    return this.fullYear;
  }
}
